import { API_BASE } from "../../../consts";
import authService from "../../../services/AuthService";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);
const jsonOrThrow = async (resp) => {
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const err = new Error(data.message || data.error || `Request failed: ${resp.status}`);
        err.status = resp.status;
        err.detail = data.detail;
        throw err;
    }
    return data;
};

export const listOrders = (fulfillment, status, q, page = 1, pageSize = 25) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (fulfillment) params.set("fulfillment", fulfillment);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    return api(`/api/orders?${params}`).then(jsonOrThrow);
};

export const getOrderDetail = (purchaseOrderId) => api(`/api/orders/${purchaseOrderId}`).then(jsonOrThrow);

export const acknowledgeOrder = (purchaseOrderId, lineNumbers) =>
    api(`/api/orders/${purchaseOrderId}/acknowledge`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ line_numbers: lineNumbers }),
    }).then(jsonOrThrow);

// Walmart's shipping update only accepts plain carrier names ("UPS", not
// "UPS Ground"). Strips the service level off known carriers; anything
// unrecognized is passed through unchanged (same as before).
const toWalmartCarrier = (carrier) => {
    const c = (carrier || "").trim().toLowerCase();
    if (c.startsWith("usps")) return "USPS";
    if (c.startsWith("ups")) return "UPS";
    if (c.startsWith("fedex") || c.startsWith("fed ex")) return "FedEx";
    return carrier;
};

export const shipOrder = (purchaseOrderId, lines, shipDate) =>
    api(`/api/orders/${purchaseOrderId}/ship`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            lines: lines.map((l) => ({ ...l, carrier: toWalmartCarrier(l.carrier) })),
            ship_date: shipDate,
        }),
    }).then(jsonOrThrow);

export const cancelOrderLines = (purchaseOrderId, lines) =>
    api(`/api/orders/${purchaseOrderId}/cancel`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines }),
    }).then(jsonOrThrow);

export const enrichOrderImage = (orderRowId) =>
    api(`/api/orders/${orderRowId}/enrich-image`, { method: "POST" }).then(jsonOrThrow);

export const acknowledgeAllOrders = (status, q) =>
    api(`/api/orders/acknowledge-all`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, q }),
    }).then(jsonOrThrow);

export const getSwwCarriers = () => api("/api/orders/sww/carriers").then(jsonOrThrow);

export const downloadShippedOrderLabel = async (purchaseOrderId, lineNumber) => {
    const resp = await api(`/api/orders/${purchaseOrderId}/download-label/${lineNumber}`);
    if (!resp.ok) {
        throw new Error("NOT FOUND");
    }
    return resp.blob();
};

export const fetchSwwLabelBlob = async (carrierShortName, trackingNo) => {
    const resp = await api(`/api/orders/sww/label/${carrierShortName}/${trackingNo}`);
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || "Failed to download label");
    }
    return resp.blob();
};

export const getShippingEstimates = (purchaseOrderId, body) =>
    api(`/api/orders/${purchaseOrderId}/sww-estimate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    }).then(jsonOrThrow);

export const buyShippingLabel = (purchaseOrderId, body) =>
    api(`/api/orders/${purchaseOrderId}/sww-buy-label`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    }).then(jsonOrThrow);

export const discardSwwLabel = (carrierShortName, trackingNo) =>
    api(`/api/orders/sww/label/${carrierShortName}/${trackingNo}`, { method: "DELETE" }).then(jsonOrThrow);

export const getDeliverBy = (purchaseOrderId) =>
    api(`/api/orders/${purchaseOrderId}/deliver-by`).then(jsonOrThrow);